"use client";

import { useLayoutEffect, useState } from "react";

import { useNavbarSettings } from "~/context/NavbarSettingsProvider";

import { NAVBAR_HEIGHT } from "./navbar.consts";

/**
 * Transparent while the route's trigger sits below the navbar, opaque once it slides
 * under; transparent with no trigger, which is an opting-in route's first paint. Owns
 * its scroll subscription — `useScrollPosition` re-rendered the navbar every frame.
 */
export const useNavbarOpacity = (): { isTransparent: boolean } => {
  const { opaqueTrigger } = useNavbarSettings();
  const [isTransparent, setIsTransparent] = useState(true);

  // Layout effect, not effect: landing mid-page — a restored scroll position, or an
  // anchor — has the trigger already above the navbar, and measuring after paint
  // shows one frame of transparent bar over content it does not belong on.
  useLayoutEffect(() => {
    if (!opaqueTrigger) {
      return;
    }

    // Flag set *before* the frame is requested, handle kept separately: a synchronous
    // rAF resets before the assignment lands and would strand a stale handle.
    let isFrameScheduled = false;
    let frameHandle: number | null = null;

    const measure = () => {
      // `setState` with an unchanged boolean is a no-op for React, so the per-frame
      // work stops here rather than propagating into a render.
      setIsTransparent(
        opaqueTrigger.getBoundingClientRect().top >= NAVBAR_HEIGHT,
      );
      isFrameScheduled = false;
    };

    const scheduleMeasure = () => {
      if (isFrameScheduled) {
        return;
      }

      isFrameScheduled = true;
      frameHandle = window.requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener("scroll", scheduleMeasure, { passive: true });
    // The trigger's offset depends on the hero's height, which is a fraction of the
    // viewport — so a resize moves it with no scroll event to notice.
    window.addEventListener("resize", scheduleMeasure);

    return () => {
      window.removeEventListener("scroll", scheduleMeasure);
      window.removeEventListener("resize", scheduleMeasure);

      if (isFrameScheduled && frameHandle !== null) {
        window.cancelAnimationFrame(frameHandle);
      }
    };
  }, [opaqueTrigger]);

  return { isTransparent };
};
